import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Search, FolderSearch, Download } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["all", "KYC", "FINANCIAL", "BANK_STATEMENT", "GST_RETURN", "INVOICE", "STOCK_STATEMENT", "COLLATERAL", "INSURANCE", "PHOTO", "VIDEO", "OTHER"];

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, category: category !== "all" ? category : undefined };
      const res = await api.get("/documents", { params });
      setDocs(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [category]);

  const download = async (d) => {
    const token = localStorage.getItem("acas_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Document Management System (DMS)" subtitle="Across all applications. SHA-256 hashed, tagged, retrievable." />
      <Card className="p-3 flex flex-col md:flex-row gap-2 md:items-center">
        <form onSubmit={(e) => { e.preventDefault(); fetchData(); }} className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search filename…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="dms-search-input" />
        </form>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="md:w-[200px]" data-testid="dms-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="secondary" onClick={fetchData}>Refresh</Button>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2.5">File</th><th className="text-left px-3 py-2.5">Category</th><th className="text-left px-3 py-2.5">App</th><th className="text-right px-3 py-2.5">Size</th><th className="text-left px-3 py-2.5">SHA-256</th><th className="text-left px-3 py-2.5">Uploaded By</th><th className="text-left px-3 py-2.5">When</th><th></th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
              {!loading && docs.length === 0 && <tr><td colSpan={8} className="text-center py-12"><FolderSearch className="h-7 w-7 mx-auto mb-2 text-muted-foreground/60" /><div className="text-sm text-muted-foreground">No documents found.</div></td></tr>}
              {docs.map(d => (
                <tr key={d.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{d.filename}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{d.category}</Badge></td>
                  <td className="px-3 py-2 text-xs"><Link to={`/applications/${d.application_id}`} className="underline text-accent">{(d.application_id || "").slice(0, 8)}…</Link></td>
                  <td className="px-3 py-2 num-right">{Math.round((d.size || 0) / 1024)} KB</td>
                  <td className="px-3 py-2 mono text-[10px] text-muted-foreground">{(d.sha256 || "").slice(0, 12)}…</td>
                  <td className="px-3 py-2 text-xs">{d.uploaded_by_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground mono">{fmtDate(d.uploaded_at)}</td>
                  <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" onClick={() => download(d)}><Download className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
